// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable hydrogen/prefer-image-component */
import {useEffect, useCallback, useState, useRef, useMemo} from 'react';

import {
  useProductOptions,
  useUrl,
  OptionWithValues,
  useCart,
  Image as ImageHydrogen,
} from '@shopify/hydrogen';

import {TextDEPRECATED, Button, ProductOptions, Input} from '~/components';
import {
  ANALYTIC_EVENT_NAMES,
  CUSTOM_ATTRIBUTES_KEYS,
  DATA_TEST_IDS,
  ELEMENT_IDS,
  I_DONT_KNOW_OPTION,
  PRODUCT_TYPE,
  QUERY_PARAM_KEYS,
  TAGS,
} from '~/lib/const';
import {
  Image,
  Metafield,
  Product,
  ProductVariant,
} from '@shopify/hydrogen/storefront-api-types';
import ProductAddons from '~/components/product/ProductAddons.client';
import {
  ContentModalEntity,
  SiteSettingsEntity,
  StoreLocationEntity,
} from '~/types';
import ProductDetailHeader from '~/components/product/ProductDetailHeader';
import {useCartDrawer} from '~/components/contexts/CartDrawerProvider.client';
import normalizeAdditionalFields from '~/lib/normalize/normalizeAdditionalFields';
import {SizeGuideModal} from '~/components/SizeGuideModal.client';
import iconStoreLocation from '../../assets/icons/icon-store-location.svg';
import {Modal} from '../Modal.client';
import SanityImage from '../media/SanityImage.client';
import {
  ClientAnalytics,
  SelectedOptions,
  useNavigate,
} from '@shopify/hydrogen/client';
import {
  findVariantBasedOnSelectedOptions,
  getNumberID,
  getOptions,
  getDiscountedOptions,
  getAvailableOptions,
  hashCode,
  preventDefault,
  getMissingOptionsInSearchParams,
} from '~/lib';
import {
  ProductBundleEntity,
  ProductBundleParts,
} from '~/types/ProductBundleEntity';
import iconCheck from '../../assets/icons/icon-check.svg';
import clsx from 'clsx';

function getProductAttributes(productAddons?: Product[]) {
  const attributes: Array<{
    key: string;
    value: string;
  }> = [];

  for (const product of productAddons ?? []) {
    const currentAddonVariant = product.variants?.nodes?.[0];

    if (!currentAddonVariant) {
      console.error(
        "ProductForm: couldn't find a addon product variant",
        product,
      );
      return [];
    }

    const id = currentAddonVariant.id.split('/').pop() ?? '';
    const price = currentAddonVariant.priceV2?.amount;

    attributes.push(
      {
        key: `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_VARIANT_ID}-${id}`,
        value: currentAddonVariant.id,
      },
      {
        key: `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_NAME}-${id}`,
        value: product.title,
      },
      {
        key: `${CUSTOM_ATTRIBUTES_KEYS.ADDON_PRODUCT_PRICE}-${id}`,
        value: Number(price) >= 0 ? String(price) : '',
      },
    );
  }

  return attributes;
}

export function ProductForm({
  product,
  productAddonsTitle,
  productAddonModals,
  productAddons,
  additionalFields,
  productOptionsModals,
  stores,
  siteSettings,
}: {
  product: Product & ProductBundleEntity;
  productAddonsTitle?: string[];
  productAddonModals?: ContentModalEntity[];
  productAddons?: Product[];
  additionalFields: Metafield;
  productOptionsModals?: Record<string, ContentModalEntity>;
  stores?: StoreLocationEntity[];
  siteSettings: SiteSettingsEntity | undefined;
}) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const {lines, linesAdd} = useCart();
  const {pathname, search} = useUrl();
  const pathnameFirst = useRef(pathname);
  const {openDrawer: openCart} = useCartDrawer();
  const additionalFormRef = useRef<HTMLFormElement>(null);
  const [params, setParams] = useState(new URLSearchParams(search));
  const [isLoading, setLoading] = useState(false);
  const [openStoreModal, setOpenStoreModal] = useState(false);
  const [optionsRequiredErrors, setOptionsRequiredErrors] = useState<
    Record<string, boolean>
  >({});

  const [showBootTreatmentModal, setShowBootTreatmentModal] = useState(false);
  const [bootTreatmentSelectedBoot, setBootTreatmentSelectedBoot] =
    useState<number>(0);

  const {
    setSelectedOption,
    selectedOptions,
    selectedVariant: mainProductSelectedVariant,
  } = useProductOptions();
  const additionalFieldsNormalized =
    normalizeAdditionalFields(additionalFields);

  const options = useMemo(
    () => getOptions(product.variants.nodes),
    [product.variants],
  );

  const discountedOptions = useMemo(
    () => getDiscountedOptions(product.variants.nodes),
    [product.variants],
  );

  const availableOptions = useMemo(
    () =>
      getAvailableOptions(
        product.variants.nodes,
        selectedOptions as SelectedOptions | undefined,
        options,
      ),
    [product.variants, selectedOptions, options],
  );

  const isMainProductOutOfStock = !mainProductSelectedVariant?.availableForSale;

  const isEveryVariantDiscontinued = useMemo(
    () =>
      // @ts-ignore
      product.variants?.nodes?.every((variant) => variant?.discontinued?.value),
    [product.variants],
  );

  const paramsString = params.toString();

  const isBOPISOnly = useMemo(
    () => product.tags?.includes(TAGS.BOPIS_ONLY),
    [product.tags],
  );
  const hasComingSoonTag = useMemo(
    () => product.tags?.includes(TAGS.COMING_SOON),
    [product.tags],
  );

  // Bundle 1. Read the bundle type and create the bundle products using the metafields
  // And get the options for each product
  const bundleProducts = useMemo(() => {
    if (product.bundleType?.value === 'kdeal-snowboard') {
      const products: ProductBundleParts = {
        type: 'kdeal-snowboard',
        ski: product.bundleSki?.reference,
        binding: product.bundleSnowboardBinding?.reference,
      };

      if (products.ski) {
        products.ski.options = getOptions(products.ski.variants.nodes);
        products.ski.availableOptions = getAvailableOptions(
          products.ski.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
          products.ski.options,
        );
      }

      if (products.binding) {
        products.binding.options = getOptions(products.binding.variants.nodes);
      }

      return products;
    } else if (product.bundleType?.value === 'kdeal-ski') {
      const products: ProductBundleParts = {
        type: 'kdeal-ski',
        ski: product.bundleSki?.reference,
        bindingVariant: product.bundleBinding?.reference,
      };

      if (products.ski) {
        products.ski.options = getOptions(products.ski.variants.nodes);
        products.ski.availableOptions = getAvailableOptions(
          products.ski.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
          products.ski.options,
        );
      }

      return products;
    } else if (product.bundleType?.value === 'pkg-ski') {
      const products: ProductBundleParts = {
        type: 'pkg-ski',
        ski: product.bundleSki?.reference,
        pole: product.bundlePole?.reference,
        boot: product.bundleBoot?.reference,
      };

      if (products.ski) {
        products.ski.options = getOptions(products.ski.variants.nodes);
        products.ski.availableOptions = getAvailableOptions(
          products.ski.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
          products.ski.options,
        );
      }

      if (products.pole) {
        products.pole.options = getOptions(products.pole.variants.nodes);
      }

      if (products.boot) {
        products.boot.options = getOptions(products.boot.variants.nodes);
      }

      return products;
    } else if (product.bundleType?.value === 'pkg-snowboard') {
      const products: ProductBundleParts = {
        type: 'pkg-snowboard',
        ski: product.bundleSki?.reference,
        binding: product.bundleSnowboardBinding?.reference,
        boot: product.bundleBoot?.reference,
      };

      if (products.ski) {
        products.ski.options = getOptions(products.ski.variants.nodes);
        products.ski.availableOptions = getAvailableOptions(
          products.ski.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
          products.ski.options,
        );
      }

      if (products.binding) {
        products.binding.options = getOptions(products.binding.variants.nodes);
      }

      if (products.boot) {
        products.boot.options = getOptions(products.boot.variants.nodes);
      }

      return products;
    } else if (product.bundleType?.value === 'simple') {
      // availableForSale == false => out of stock
      // availableForSale == true && quantity == 0 => no track quantity
      // availableForSale == true && quantity > 0 => sell only N items

      const products: ProductBundleParts = {
        type: 'simple',
        products: product.bundleSimpleProducts?.references?.edges,
        variants: product.bundleSimpleVariants?.references?.edges,
      };

      if (products.products) {
        products.products = products.products.map((edge) => {
          edge.node.options = getOptions(edge.node.variants.nodes);

          return edge;
        });
      }

      return products;
    }

    return undefined;
  }, [product, selectedOptions]);

  // Bundle 2. Select the main product variant based on the selected options
  // We use this to check if the main product is out of stock
  // But if the bundle is a simple one, we just get the first one
  // Because the stock of the simple bundle doesn't depend on
  // the stock of the main product, but on the stock off all the products
  const selectedBundleMainProductVariant = useMemo(() => {
    if (bundleProducts?.type === 'simple') {
      if ((bundleProducts.products?.length ?? 0) > 0) {
        return findVariantBasedOnSelectedOptions(
          bundleProducts.products?.[0].node.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
        );
      } else if ((bundleProducts.variants?.length ?? 0) > 0) {
        return bundleProducts.variants?.[0].node;
      }

      return null;
    }

    const ski = bundleProducts?.ski;
    if (ski == null) {
      return undefined;
    }

    const selectedVariant = findVariantBasedOnSelectedOptions(
      ski.variants.nodes,
      selectedOptions as SelectedOptions | undefined,
    );

    if (selectedVariant) {
      return selectedVariant;
    }

    return ski.variants.nodes.find((variant) => variant.availableForSale);
  }, [bundleProducts, selectedOptions]);

  const selectedVariant = bundleProducts
    ? selectedBundleMainProductVariant
    : mainProductSelectedVariant;

  useEffect(() => {
    // ***
    // For some reason this useEffect called even after going to another page
    // which adds the paramsString to that page unnecessarily.
    // So, we will look for pathname match to run this function.
    if (pathnameFirst.current === pathname) {
      window.history.replaceState(
        {},
        '',
        `${pathname}?${paramsString.toLowerCase()}`,
      );
    }
  }, [paramsString, pathname]);

  useEffect(() => {
    setTimeout(() => {
      (options as OptionWithValues[]).map(({name, values}) => {
        if (bundleProducts) return;

        // if (!params) return;
        const newParams = new URLSearchParams(search);
        const currentValue =
          newParams.get(encodeURIComponent(name.toLowerCase())) || null;
        if (currentValue) {
          const matchedValue = values.filter(
            (value) => value.toLowerCase() === currentValue.toLowerCase(),
          );

          setSelectedOption(name, matchedValue[0]);
        } else {
          newParams.set(
            encodeURIComponent(name.toLowerCase()),
            selectedOptions![name]!.toLowerCase(),
          );
          setParams(newParams);
        }
      });

      // Bundle 3. Get the options from the URL and set them as selected
      if (bundleProducts) {
        for (const type of Object.keys(bundleProducts ?? {})) {
          if (type === 'type') {
            continue;
          }

          if (bundleProducts?.type === 'simple') {
            const products = bundleProducts?.products;

            if (products) {
              products.map(({node: product}) => {
                product.options.map(({name, values}) => {
                  const newParams = new URLSearchParams(search);
                  const currentValue =
                    newParams.get(encodeURIComponent(name.toLowerCase())) ||
                    null;
                  if (currentValue) {
                    const matchedValue = values.filter(
                      (value) =>
                        encodeURIComponent(value.toLowerCase()) ===
                        currentValue,
                    );

                    setSelectedOption(name, matchedValue[0]);
                  }
                });
              });
            }
          } else {
            const product =
              bundleProducts?.type === 'pkg-ski'
                ? bundleProducts?.[type as 'ski' | 'pole' | 'boot']
                : bundleProducts?.type === 'pkg-snowboard'
                ? bundleProducts?.[type as 'ski' | 'binding' | 'boot']
                : bundleProducts?.type === 'kdeal-snowboard'
                ? bundleProducts?.[type as 'ski' | 'binding']
                : bundleProducts?.[type as 'ski'];

            product?.options?.map(({name, values}) => {
              const newParams = new URLSearchParams(search);
              const currentValue =
                newParams.get(encodeURIComponent(name.toLowerCase())) || null;
              if (currentValue) {
                const matchedValue = values.filter(
                  (value) =>
                    encodeURIComponent(value.toLowerCase()) === currentValue,
                );

                setSelectedOption(name, matchedValue[0]);
              }
            });
          }
        }

        // Get the main bundle variant and add the options to the url if there isn't
        // Also add the options to each product that is part of the bundle if there is some available
        if (selectedBundleMainProductVariant) {
          const variantsToSelect: ProductVariant[][] = [];

          if (bundleProducts.type === 'pkg-ski') {
            variantsToSelect.push(bundleProducts.boot?.variants?.nodes ?? []);
            variantsToSelect.push(bundleProducts.pole?.variants?.nodes ?? []);
          } else if (bundleProducts.type === 'pkg-snowboard') {
            variantsToSelect.push(bundleProducts.boot?.variants?.nodes ?? []);
          } else if (bundleProducts.type === 'kdeal-snowboard') {
            variantsToSelect.push(
              bundleProducts.binding?.variants?.nodes ?? [],
            );
          } else if (bundleProducts.type === 'simple') {
            for (const product of bundleProducts.products ?? []) {
              variantsToSelect.push(product?.node?.variants?.nodes ?? []);
            }
          }

          const newOptions = getMissingOptionsInSearchParams(
            params,
            selectedBundleMainProductVariant,
            variantsToSelect,
          );

          const searchParams = new URLSearchParams(params?.toString() ?? '');

          for (const key of Object.keys(newOptions)) {
            setSelectedOption(key, newOptions[key]);

            searchParams.set(
              encodeURIComponent(key.toLowerCase()),
              encodeURIComponent(newOptions[key]),
            );
          }

          setParams(searchParams);
        }
      } else if (mainProductSelectedVariant) {
        const newOptions = getMissingOptionsInSearchParams(
          params,
          mainProductSelectedVariant as ProductVariant,
          [],
        );

        const searchParams = new URLSearchParams(params?.toString() ?? '');

        for (const key of Object.keys(newOptions)) {
          setSelectedOption(key, newOptions[key]);

          searchParams.set(
            encodeURIComponent(key.toLowerCase()),
            newOptions[key],
          );
        }

        setParams(searchParams);
      }
    }, 0);

    // *** Adding dependencies calls this function repeatedly
    // *** There is a lot of missing dependencies here coming from boilerplate code
    // *** We will disable eslint for now but be careful and fix this if possible when developing this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (name: string, value: string) => {
      setOptionsRequiredErrors((prev) => ({
        ...prev,
        [name]: false,
      }));

      setSelectedOption(name, value);
      // Update params
      const searchParams = new URLSearchParams(params?.toString() ?? '');
      searchParams.set(encodeURIComponent(name.toLowerCase()), value);
      setParams(searchParams);
    },
    [setSelectedOption, params],
  );

  const handleClickAddon = useCallback(
    (index: number) => {
      const key = `${QUERY_PARAM_KEYS.ADDON}-${index}`;

      const searchParams = new URLSearchParams(params.toString());
      const addonAdded = searchParams.get(key) != null;

      if (addonAdded) {
        searchParams.delete(key);
        setParams(searchParams);
      } else {
        searchParams.set(key, '1');
        setParams(searchParams);
      }
    },
    [params],
  );

  const cartQuantities = useMemo(() => {
    return lines.reduce((acc, line) => {
      const {quantity, merchandise} = line;

      const {id} = merchandise;

      if (acc[id]) {
        acc[id] += quantity;
      } else {
        acc[id] = quantity;
      }

      return acc;
    }, {} as Record<string, number>);
  }, [lines]);

  const currentProductQuantities = useMemo(() => {
    const maxQuantities: Record<string, number> = {};

    if (bundleProducts?.type === 'simple') {
      for (let i = 0; i < (bundleProducts.products ?? []).length; i++) {
        const product = bundleProducts.products?.[i];

        const selectedProduct = findVariantBasedOnSelectedOptions(
          product?.node.variants?.nodes,
          selectedOptions as SelectedOptions | undefined,
        );

        if (selectedProduct) {
          const productQuantity = selectedProduct.quantityAvailable ?? 0;

          if (productQuantity > 0) {
            maxQuantities[selectedProduct.id] = productQuantity;
          } else if (!selectedProduct.availableForSale) {
            maxQuantities[selectedProduct.id] = 0;
          }
        }
      }

      for (let i = 0; i < (bundleProducts.variants ?? []).length; i++) {
        const variant = bundleProducts.variants?.[i];

        if (variant) {
          const variantQuantity = variant.node.quantityAvailable ?? 0;

          if (variantQuantity > 0) {
            maxQuantities[variant.node.id] = variantQuantity;
          } else if (!variant.node.availableForSale) {
            maxQuantities[variant.node.id] = 0;
          }
        }
      }
    } else {
      if (
        selectedVariant &&
        selectedVariant.id &&
        selectedVariant.availableForSale &&
        selectedVariant.quantityAvailable &&
        selectedVariant.quantityAvailable > 0
      ) {
        maxQuantities[selectedVariant.id] = selectedVariant.quantityAvailable;
      }
    }

    return maxQuantities;
  }, [bundleProducts, selectedOptions, selectedVariant]);

  const allAvailableProductsAlreadyInCart = useMemo(() => {
    let invalid = false;

    for (const productVariantID of Object.keys(currentProductQuantities)) {
      if (
        cartQuantities[productVariantID] >=
        currentProductQuantities[productVariantID]
      ) {
        invalid = true;
        break;
      }
    }

    return invalid;
  }, [currentProductQuantities, cartQuantities]);

  // Bundle 4. Check if the main product is out of stock
  // This is used to disable the add to cart button
  // But if the bundle is a simple one we need to check if all the products are out of stock
  const isOutOfStock = useMemo(() => {
    if (bundleProducts?.type === 'simple') {
      let isAnyProductsORVariantsOutOfStock = false;

      for (const product of bundleProducts.products ?? []) {
        const selected = findVariantBasedOnSelectedOptions(
          product.node.variants.nodes,
          selectedOptions as SelectedOptions | undefined,
        );

        if (selected && !selected.availableForSale) {
          isAnyProductsORVariantsOutOfStock = true;
          break;
        }
      }

      for (const variant of bundleProducts.variants ?? []) {
        if (!variant.node.availableForSale) {
          isAnyProductsORVariantsOutOfStock = true;
          break;
        }
      }

      return (
        isAnyProductsORVariantsOutOfStock || allAvailableProductsAlreadyInCart
      );
    } else {
      return (
        (bundleProducts?.ski
          ? !selectedBundleMainProductVariant?.availableForSale
          : isMainProductOutOfStock) || allAvailableProductsAlreadyInCart
      );
    }
  }, [
    selectedOptions,
    bundleProducts,
    isMainProductOutOfStock,
    allAvailableProductsAlreadyInCart,
    selectedBundleMainProductVariant?.availableForSale,
  ]);

  // Bundle 5. Get all the boot options inside the boot bundle
  const bootsOptions = useMemo(() => {
    if (
      bundleProducts?.type === 'kdeal-snowboard' ||
      bundleProducts?.type === 'kdeal-ski' ||
      bundleProducts?.type === 'simple'
    ) {
      return [];
    }

    const allBoots =
      bundleProducts?.boot?.bundleBootOptions?.references?.edges ?? [];
    const validBoots: typeof allBoots = [];

    const bootSizeOptionKey = Object.keys(selectedOptions ?? {}).find(
      (key) =>
        key.toLowerCase().includes('boot') &&
        key.toLowerCase().includes('size'),
    );

    const bootSizeOptionValue = selectedOptions?.[bootSizeOptionKey ?? ''];

    if (bootSizeOptionValue && bootSizeOptionValue !== I_DONT_KNOW_OPTION) {
      for (const boot of allBoots) {
        for (const variant of boot.node?.variants.nodes ?? []) {
          const size = variant.selectedOptions?.find(
            (o) =>
              o.name.toLowerCase().includes('boot') &&
              o.name.toLowerCase().includes('size'),
          );

          const quantitiesOnCart = cartQuantities[variant.id] ?? 0;

          if (size?.value === bootSizeOptionValue && variant.availableForSale) {
            if (
              variant.quantityAvailable === 0 ||
              quantitiesOnCart < (variant.quantityAvailable ?? 0)
            ) {
              validBoots.push(boot);
            }
          }
        }
      }
    }

    return validBoots;
  }, [bundleProducts, selectedOptions, cartQuantities]);

  const handleClickAddToCart = useCallback(
    async (skipBootTreatment?: boolean) => {
      // Bundle 6. Check if the user selected all the required options
      // All the options are required
      if (bundleProducts && !skipBootTreatment) {
        const optionsErrors: Record<string, boolean> = {};

        if (bundleProducts.type === 'simple') {
          for (const product of bundleProducts.products ?? []) {
            for (const option of product.node.options ?? []) {
              const name = option.name;

              if (!selectedOptions?.[name]) {
                optionsErrors[name] = true;
              }
            }
          }
        } else {
          for (const option of bundleProducts?.ski?.options ?? []) {
            const name = option.name;

            if (!selectedOptions?.[name]) {
              optionsErrors[name] = true;
            }
          }
        }

        if (bundleProducts.type === 'pkg-ski') {
          for (const option of bundleProducts?.pole?.options ?? []) {
            const name = option.name;

            if (!selectedOptions?.[name]) {
              optionsErrors[name] = true;
            }
          }
        }

        if (
          bundleProducts.type === 'pkg-ski' ||
          bundleProducts.type === 'pkg-snowboard'
        ) {
          for (const option of bundleProducts?.boot?.options ?? []) {
            const name = option.name;

            if (!selectedOptions?.[name]) {
              optionsErrors[name] = true;
            }
          }
        }

        if (bundleProducts.type === 'kdeal-snowboard') {
          for (const option of bundleProducts?.binding?.options ?? []) {
            const name = option.name;

            if (!selectedOptions?.[name]) {
              optionsErrors[name] = true;
            }
          }
        }

        if (Object.keys(optionsErrors).length > 0) {
          setOptionsRequiredErrors(optionsErrors);
          return;
        }

        if (
          (bundleProducts.type === 'pkg-ski' ||
            bundleProducts.type === 'pkg-snowboard') &&
          bundleProducts?.boot
        ) {
          const selectedBoot = findVariantBasedOnSelectedOptions(
            bundleProducts?.boot.variants.nodes,
            selectedOptions as SelectedOptions | undefined,
          );

          const bootSizeOption = Object.keys(selectedOptions ?? {}).find(
            (key) =>
              key.toLowerCase().includes('boot') &&
              key.toLowerCase().includes('size'),
          );

          // If the user selected a boot size and the boot is not available
          // We need to show the boot treatment modal
          if (selectedBoot && bootSizeOption !== I_DONT_KNOW_OPTION) {
            const quantitiesOnCart = cartQuantities[selectedBoot.id] ?? 0;

            if (
              !selectedBoot.availableForSale ||
              (quantitiesOnCart > 0 &&
                quantitiesOnCart >= (selectedBoot.quantityAvailable ?? 0))
            ) {
              setShowBootTreatmentModal(true);
              setBootTreatmentSelectedBoot(bootsOptions.length === 0 ? -1 : 0);

              return;
            }
          }
        }
      }

      if (selectedVariant?.id == null) {
        console.error('There is no variant on this product');
        return;
      }

      try {
        setLoading(true);
        const productAttributes = getProductAttributes(productAddons);

        if (product?.vendor) {
          productAttributes.push({
            key: CUSTOM_ATTRIBUTES_KEYS.ITEM_VENDOR,
            value: product?.vendor,
          });
        }

        if (product?.productType) {
          productAttributes.push({
            key: CUSTOM_ATTRIBUTES_KEYS.ITEM_PRODUCT_TYPE,
            value: product?.productType,
          });
        }

        const onlinePriceJson = selectedVariant.onlinePrice?.value;
        if (onlinePriceJson) {
          const onlinePrice = JSON.parse(
            selectedVariant.onlinePrice?.value,
          )?.amount;

          if (onlinePrice != selectedVariant?.compareAtPriceV2?.amount) {
            productAttributes.push({
              key: '_online_price',
              value: onlinePrice,
            });
          }
        }

        // Push additional free form fields to item attributes
        if (additionalFormRef.current != null) {
          const formData = new FormData(additionalFormRef.current);
          const values = Object.fromEntries(formData);

          Object.entries(values).forEach(([key, value]) => {
            if (value != null && value !== '') {
              productAttributes.push({
                key,
                value: String(value),
              });
            }
          });
        }

        if (isBOPISOnly) {
          productAttributes.push({
            key: CUSTOM_ATTRIBUTES_KEYS.BOPIS_ONLY,
            value: 'true',
          });
        }

        const bundleProductAttributes: {
          key: string;
          value: string;
        }[] = [];

        if (bundleProducts) {
          let bundlePrice = product.variants.nodes[0].priceV2.amount;
          const bundleOnlinePriceJson =
            product.variants.nodes[0].onlinePrice?.value;
          if (bundleOnlinePriceJson) {
            bundlePrice = JSON.parse(bundleOnlinePriceJson)?.amount;
          }
          bundleProductAttributes.push(
            {
              key: '_bundle_title',
              value: product.title,
            },
            {
              key: '_bundle_price',
              value: bundlePrice,
            },
          );

          bundleProductAttributes.push(
            {
              key: '_bundle', //CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
              value: product.title,
            },
            {
              key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_ID,
              value: product.variants.nodes[0].id,
            },
          );

          Object.keys(selectedOptions).forEach((k) => {
            if (k.toLowerCase() != 'title') {
              bundleProductAttributes.push({
                key: `_option_${k.replace(' ', '_')}`,
                value: selectedOptions[k],
              });
            }
          });

          // productAttributes.push(...bundleProductAttributes);
          // productAttributes.push({
          //   key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_MAIN,
          //   value: 'true',
          // });
        }

        const itemsToTrack: Array<{
          item_name: string;
          item_id: number;
          item_brand: string;
          currency: string;
          price: number;
          item_category: string;
          item_variant: string;
          quantity: number;
        }> = [];

        const itemsToAdd: {
          merchandiseId: string;
          attributes: {
            key: string;
            value: string;
          }[];
          quantity: number;
        }[] = [];

        let maxQuantity = Number.POSITIVE_INFINITY;

        // Bundle 7. Add the products to the cart
        if (bundleProducts) {
          let selectedBoot: Product | undefined;
          let selectedBootVariant: ProductVariant | undefined;

          if (
            bundleProducts.type === 'pkg-ski' ||
            bundleProducts.type === 'pkg-snowboard'
          ) {
            // Bundle 7.1. Add the boot to the cart
            if (bundleProducts.boot) {
              if (bootTreatmentSelectedBoot === -1) {
                selectedBoot = bundleProducts.boot;
                selectedBootVariant = selectedBoot?.variants?.nodes.find(
                  (v) => {
                    for (const option of v.selectedOptions) {
                      if (option.value === I_DONT_KNOW_OPTION) {
                        return v;
                      }
                    }

                    return undefined;
                  },
                );
              } else {
                selectedBoot =
                  showBootTreatmentModal && bootTreatmentSelectedBoot >= 0
                    ? bootsOptions?.[bootTreatmentSelectedBoot].node
                    : bundleProducts.boot;

                selectedBootVariant = selectedBoot?.variants?.nodes?.find(
                  (v) => {
                    for (const option of v.selectedOptions) {
                      if (
                        option.name.toLowerCase().includes('boot') &&
                        option.name.toLowerCase().includes('size')
                      ) {
                        if (selectedOptions?.[option.name] === option.value) {
                          return v;
                        }
                      }
                    }
                  },
                );

                maxQuantity = Math.min(
                  maxQuantity,
                  selectedBootVariant?.quantityAvailable ?? 0,
                );
              }

              if (selectedBootVariant) {
                itemsToAdd.push({
                  merchandiseId: selectedBootVariant.id,
                  quantity: 1,
                  attributes: [
                    ...bundleProductAttributes,
                    {
                      key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                      value: PRODUCT_TYPE.BOOT.toLowerCase(),
                    },
                    {
                      key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
                      value: String(
                        selectedBootVariant?.quantityAvailable ?? 0,
                      ),
                    },
                  ],
                });
              }
            }
          }

          // Bundle 7.2. Add the products and variants in the simple bundle to the cart
          if (bundleProducts.type === 'simple') {
            for (let i = 0; i < (bundleProducts.products ?? []).length; i++) {
              const product = bundleProducts.products?.[i];

              const selectedProduct = findVariantBasedOnSelectedOptions(
                product?.node.variants?.nodes,
                selectedOptions as SelectedOptions | undefined,
              );

              if (selectedProduct) {
                const productQuantity = selectedProduct.quantityAvailable ?? 0;

                if (productQuantity > 0) {
                  maxQuantity = Math.min(maxQuantity, productQuantity);
                }

                itemsToAdd.push({
                  merchandiseId: selectedProduct.id,
                  quantity: 1,
                  attributes: [
                    ...bundleProductAttributes,
                    ...(productQuantity > 0
                      ? [
                          {
                            key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
                            value: String(productQuantity),
                          },
                        ]
                      : []),
                    {
                      key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                      value: PRODUCT_TYPE.PRODUCT.toLowerCase() + i,
                    },
                  ],
                });
              }
            }

            for (let i = 0; i < (bundleProducts.variants ?? []).length; i++) {
              const variant = bundleProducts.variants?.[i];

              if (variant) {
                const variantQuantity = variant.node.quantityAvailable ?? 0;

                if (variantQuantity > 0) {
                  maxQuantity = Math.min(maxQuantity, variantQuantity);
                }

                itemsToAdd.push({
                  merchandiseId: variant.node.id,
                  quantity: 1,
                  attributes: [
                    ...bundleProductAttributes,
                    ...(variantQuantity > 0
                      ? [
                          {
                            key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
                            value: String(variantQuantity),
                          },
                        ]
                      : []),
                    {
                      key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                      value: PRODUCT_TYPE.VARIANT.toLowerCase() + i,
                    },
                  ],
                });
              }
            }
          }

          // Bundle 7.3. Add the pole to the cart
          if (bundleProducts.type === 'pkg-ski') {
            const selectedPole = findVariantBasedOnSelectedOptions(
              bundleProducts.pole?.variants?.nodes,
              selectedOptions as SelectedOptions | undefined,
            );

            if (selectedPole) {
              itemsToAdd.push({
                merchandiseId: selectedPole.id,
                quantity: 1,
                attributes: [
                  ...bundleProductAttributes,
                  {
                    key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                    value: PRODUCT_TYPE.POLE.toLowerCase(),
                  },
                ],
              });
            }
          }

          // Bundle 7.4. Add the binding to the cart
          if (bundleProducts.type === 'kdeal-snowboard') {
            const selectedBinding = findVariantBasedOnSelectedOptions(
              bundleProducts.binding?.variants?.nodes,
              selectedOptions as SelectedOptions | undefined,
            );

            if (selectedBinding) {
              itemsToAdd.push({
                merchandiseId: selectedBinding.id,
                quantity: 1,
                attributes: [
                  ...bundleProductAttributes,
                  {
                    key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                    value: PRODUCT_TYPE.BINDING.toLowerCase(),
                  },
                ],
              });
            }
          }

          // Bundle 7.5. Add the snowboard binding to the cart
          if (bundleProducts.type === 'pkg-snowboard') {
            let selectedBinding: ProductVariant | undefined;

            const bootSizeOption = selectedBootVariant?.selectedOptions.find(
              (option) =>
                option.name.toLowerCase().includes('boot') &&
                option.name.toLowerCase().includes('size'),
            );

            // The variant of the binding depends on the boot size
            // If the user doesn't know the boot size, we select the first variant
            if (bootSizeOption?.value === I_DONT_KNOW_OPTION) {
              selectedBinding = bundleProducts.binding?.variants?.nodes?.[0];
            } else {
              for (const variant of bundleProducts.binding?.variants.nodes ??
                []) {
                if (variant.bundleBindingSizeRange) {
                  if (variant.bundleBindingSizeRange?.value?.includes('-')) {
                    const [min, max] =
                      variant.bundleBindingSizeRange.value.split('-');

                    const bootSize = Number(bootSizeOption?.value);

                    if (bootSize >= Number(min) && bootSize <= Number(max)) {
                      selectedBinding = variant;
                      break;
                    }
                  } else {
                    const bootSize = Number(bootSizeOption?.value);

                    if (
                      bootSize === Number(variant.bundleBindingSizeRange?.value)
                    ) {
                      selectedBinding = variant;
                      break;
                    }
                  }
                }
              }
            }

            if (selectedBinding) {
              itemsToAdd.push({
                merchandiseId: selectedBinding.id,
                quantity: 1,
                attributes: [
                  ...bundleProductAttributes,
                  {
                    key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                    value: PRODUCT_TYPE.BINDING.toLowerCase(),
                  },
                ],
              });
            }
          }

          // Bundle 7.6. Add the ski binding to the cart
          if (bundleProducts.type === 'kdeal-ski') {
            if (bundleProducts.bindingVariant) {
              itemsToAdd.push({
                merchandiseId: bundleProducts.bindingVariant.id,
                quantity: 1,
                attributes: [
                  ...bundleProductAttributes,
                  {
                    key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
                    value: PRODUCT_TYPE.BINDING.toLowerCase(),
                  },
                ],
              });
            }
          }
        }

        itemsToTrack.push({
          currency: 'USD',
          item_brand: product?.vendor ?? '',
          item_category: product?.productType ?? '',
          item_id: getNumberID(product.id),
          item_name: product?.title ?? '',
          item_variant: selectedVariant.title ?? '',
          price: Number(selectedVariant.priceV2?.amount),
          quantity: 1,
        });

        // Base product
        if (bundleProducts) {
          const attributes = [
            ...bundleProductAttributes,
            {
              key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_TYPE,
              value: PRODUCT_TYPE.SKI,
            },
          ];

          if (selectedVariant.quantityAvailable ?? 0) {
            maxQuantity = Math.min(
              maxQuantity,
              selectedVariant.quantityAvailable ?? 0,
            );
          }

          // Add the main product to the cart
          itemsToAdd.push({
            merchandiseId: product.variants.nodes[0].id,
            attributes: [
              ...productAttributes,
              {
                key: '_bundle_main',
                value: 'true',
              },
              {
                key: 'Bundle_main',
                value: 'true',
              },
              {
                key: '_bundle', //CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_BUNDLE,
                value: product.title,
              },
            ],
            quantity: 1,
          });

          // Add the ski product to the cart if isn't a simple bundle
          if (bundleProducts.type !== 'simple') {
            itemsToAdd.push({
              merchandiseId: selectedVariant.id,
              attributes: [
                ...attributes,
                {
                  key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
                  value: String(selectedVariant.quantityAvailable ?? 0),
                },
              ],
              quantity: 1,
            });
          }
        } else {
          const item = {
            merchandiseId: selectedVariant.id.toString(),
            attributes: [
              ...productAttributes,
              {
                key: CUSTOM_ATTRIBUTES_KEYS.BUNDLE_ATTRIBUTE_QUANTITY,
                value: String(selectedVariant.quantityAvailable ?? 0),
              },
            ],
            quantity: 1,
          };

          const giftCardEligible =
            product.tags.indexOf('23-12-08 FREE GIFT CARD QUALIFYING') >= 0 &&
            parseFloat(selectedVariant.priceV2!.amount!) >= 500;

          if (giftCardEligible) {
            item.attributes.push({
              key: '_giftcardeligible',
              value: 'true',
            });
          }

          itemsToAdd.push(item);
        }

        for (let index = 0; index < (productAddons ?? []).length; index++) {
          const productAddon = productAddons![index];

          // Check if there is addon product
          const currentVariantAddon = productAddon.variants?.nodes?.[0];
          const isOutOfStock = !currentVariantAddon?.availableForSale || false;

          const addonAttributes = [
            {
              key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_VARIANT_ID,
              value: String(selectedVariant.id),
            },
            {
              key: CUSTOM_ATTRIBUTES_KEYS.ADDON_PARENT_PRODUCT_TITLE,
              value: product.title,
            },
            {
              key: 'For',
              value: product.title,
            },
          ];

          const isActiveAddon =
            params.get(`${QUERY_PARAM_KEYS.ADDON}-${index}`) === '1';

          if (
            currentVariantAddon?.id != null &&
            !isOutOfStock &&
            isActiveAddon
          ) {
            itemsToAdd.push({
              merchandiseId: currentVariantAddon.id,
              attributes: addonAttributes,
              quantity: 1,
            });

            itemsToTrack.push({
              currency: 'USD',
              item_brand: productAddon.vendor ?? '',
              item_category: productAddon.productType ?? '',
              item_id: getNumberID(productAddon.id),
              item_name: productAddon?.title ?? '',
              item_variant: currentVariantAddon.title ?? '',
              price: Number(currentVariantAddon.priceV2?.amount),
              quantity: 1,
            });
          }
        }

        if (bundleProducts) {
          let hash = '';

          for (const itemToAdd of itemsToAdd) {
            hash += itemToAdd.merchandiseId;
          }
          hash += Math.floor(Math.random() * 1000).toString();

          for (const itemToAdd of itemsToAdd) {
            itemToAdd.attributes.push({
              key: '_bundle_hash',
              value: hashCode(hash).toString(),
            });
            itemToAdd.attributes.push({
              key: 'Bundle_hash',
              value: hashCode(hash).toString(),
            });
          }
        }

        setBootTreatmentSelectedBoot(0);
        setShowBootTreatmentModal(false);

        ClientAnalytics.publish(ANALYTIC_EVENT_NAMES.ADD_TO_CART, true, {
          event: {
            _clear: true,
            event: ANALYTIC_EVENT_NAMES.ADD_TO_CART,
            ecommerce: {
              items: itemsToTrack,
            },
          },
        });

        // linesAdd doesn't return promise unfortunately.
        // Because of that, we don't know when it is added.
        linesAdd(itemsToAdd);

        // Open the cart drawer
        openCart();
      } catch (error) {
        console.error(error);
      } finally {
        setShowBootTreatmentModal(false);

        setLoading(false);
      }
    },
    [
      bundleProducts,
      selectedVariant,
      selectedOptions,
      cartQuantities,
      bootsOptions,
      productAddons,
      isBOPISOnly,
      linesAdd,
      openCart,
      product,
      bootTreatmentSelectedBoot,
      showBootTreatmentModal,
      params,
    ],
  );

  // Get the color images for all products
  // This is used to show the color option images
  const colorImages = useMemo(() => {
    const colorImages: Record<string, Image> = {};

    let variants: ProductVariant[] | undefined = [];

    if (bundleProducts) {
      if (bundleProducts.type === 'simple') {
        for (const product of bundleProducts.products ?? []) {
          variants.push(...(product.node.variants.nodes ?? []));
        }

        variants.push(
          ...(bundleProducts.variants ?? []).map((variant) => variant.node),
        );
      } else {
        variants.push(...(bundleProducts.ski?.variants.nodes ?? []));
      }

      if (bundleProducts.type === 'pkg-ski') {
        variants.push(...(bundleProducts.boot?.variants.nodes ?? []));
        variants.push(...(bundleProducts.pole?.variants.nodes ?? []));
      } else if (bundleProducts.type === 'pkg-snowboard') {
        variants.push(...(bundleProducts.boot?.variants.nodes ?? []));
      } else if (bundleProducts.type === 'kdeal-snowboard') {
        variants.push(...(bundleProducts.binding?.variants.nodes ?? []));
      }
    } else {
      variants = product.variants.nodes;
    }

    variants?.forEach((variant) => {
      const colorOptionsNames = ['color', 'frame/lens', 'frames', 'lens'];
      colorOptionsNames.forEach((optionName) => {
        const color = variant.selectedOptions.find(({name}) => {
          return name.toLowerCase() === optionName;
        })?.value;
        if (color == null) return;
        if (colorImages[color] == null && variant.image != null) {
          colorImages[color] = variant.image;
        }
      });
    });

    return colorImages;
  }, [bundleProducts, product.variants.nodes]);

  useEffect(() => {
    ClientAnalytics.publish(ANALYTIC_EVENT_NAMES.VIEW_ITEM, true, {
      event: {
        _clear: true,
        event: ANALYTIC_EVENT_NAMES.VIEW_ITEM,
        ecommerce: {
          items: [
            {
              item_name: product.title,
              item_id: getNumberID(product.id),
              item_brand: product.vendor,
              currency: 'USD',
              price: product.variants?.nodes?.[0]?.priceV2?.amount ?? 0,
              item_category: product.productType,
              item_variant:
                product.variants?.nodes?.[0]?.title ?? product.title,
            },
          ],
        },
      },
    });
  }, [product]);

  // let elementRef: Element | null = null;

  // if (typeof window === 'object') {
  //   elementRef = document.getElementById(ELEMENT_IDS.PRODUCT_STICKY_HEADER);
  // }

  return (
    <div data-testid={DATA_TEST_IDS.PRODUCT_FORM} className="grid">
      {/* {elementRef &&
        createPortal(
          <ProductHeaderSticky
            title={product.title}
            isOutOfStock={isOutOfStock}
            isLoading={isLoading}
            handleClickAddToCart={() => handleClickAddToCart(false)}
          />,
          elementRef,
        )} */}

      <div className="grid gap-10">
        {bundleProducts ? (
          <div
            data-testid={DATA_TEST_IDS.BUNDLE_PRODUCT}
            className="flex flex-col space-y-[30px]"
          >
            {bundleProducts.type === 'simple' ? (
              <>
                {bundleProducts.products?.map((product) => (
                  <>
                    <ProductOptionsList
                      testid={DATA_TEST_IDS.BUNDLE_TYPE('simple')}
                      type="bundle"
                      prefix="ski"
                      options={product.node.options}
                      params={params}
                      colorImages={colorImages}
                      handleChange={handleChange}
                      productOptionsModals={productOptionsModals}
                      errors={optionsRequiredErrors}
                    />

                    {additionalFieldsNormalized != null &&
                      additionalFieldsNormalized.length > 0 && (
                        <form
                          action={'#'}
                          ref={additionalFormRef}
                          onSubmit={preventDefault}
                        >
                          {additionalFieldsNormalized?.map(
                            (fieldTitle: string, index: number) => {
                              return (
                                <div
                                  key={fieldTitle}
                                  className={clsx(index > 0 && 'mt-4')}
                                >
                                  <ProductDetailHeader
                                    title={fieldTitle?.toUpperCase()}
                                    data={
                                      productOptionsModals?.[
                                        fieldTitle.toUpperCase()
                                      ]
                                    }
                                  />
                                  <Input
                                    data-testid={DATA_TEST_IDS.INPUT(
                                      fieldTitle,
                                    )}
                                    name={fieldTitle}
                                    type={'text'}
                                    className="md:w-[224px]"
                                    variant={'default'}
                                  />
                                </div>
                              );
                            },
                          )}
                        </form>
                      )}
                  </>
                ))}
              </>
            ) : (
              bundleProducts.ski && (
                <>
                  <ProductOptionsList
                    testid={DATA_TEST_IDS.BUNDLE_TYPE('bundle-ski')}
                    type="bundle"
                    prefix="ski"
                    options={bundleProducts.ski.options}
                    availableOptions={bundleProducts.ski.availableOptions}
                    discountedOptions={discountedOptions}
                    params={params}
                    colorImages={colorImages}
                    handleChange={handleChange}
                    productOptionsModals={productOptionsModals}
                    errors={optionsRequiredErrors}
                  />

                  {additionalFieldsNormalized != null &&
                    additionalFieldsNormalized.length > 0 && (
                      <form
                        action={'#'}
                        ref={additionalFormRef}
                        onSubmit={preventDefault}
                      >
                        {additionalFieldsNormalized?.map(
                          (fieldTitle: string, index: number) => {
                            return (
                              <div
                                key={fieldTitle}
                                className={clsx(index > 0 && 'mt-4')}
                              >
                                <ProductDetailHeader
                                  title={fieldTitle?.toUpperCase()}
                                  data={
                                    productOptionsModals?.[
                                      fieldTitle.toUpperCase()
                                    ]
                                  }
                                />
                                <Input
                                  data-testid={DATA_TEST_IDS.INPUT(fieldTitle)}
                                  name={fieldTitle}
                                  type={'text'}
                                  className="md:w-[224px]"
                                  variant={'default'}
                                />
                              </div>
                            );
                          },
                        )}
                      </form>
                    )}
                </>
              )
            )}

            {bundleProducts.type === 'kdeal-snowboard' &&
              bundleProducts.binding && (
                <ProductOptionsList
                  testid={DATA_TEST_IDS.BUNDLE_TYPE('kdeal-snowboard')}
                  type="bundle"
                  prefix="binding"
                  options={bundleProducts.binding.options}
                  params={params}
                  colorImages={colorImages}
                  handleChange={handleChange}
                  productOptionsModals={productOptionsModals}
                  errors={optionsRequiredErrors}
                />
              )}

            {bundleProducts.type === 'pkg-ski' ? (
              <div className="flex flex-col space-y-[30px] xl:flex-row xl:space-x-[38px] xl:space-y-0 mt-[30px]">
                {bundleProducts.boot && (
                  <ProductOptionsList
                    testid={DATA_TEST_IDS.BUNDLE_TYPE('pkg-ski')}
                    type="bundle"
                    prefix="boot"
                    options={bundleProducts.boot.options}
                    params={params}
                    colorImages={colorImages}
                    handleChange={handleChange}
                    productOptionsModals={productOptionsModals}
                    errors={optionsRequiredErrors}
                    optionsType={DATA_TEST_IDS.PRODUCT_OPTIONS_DROPDOWN}
                  />
                )}
                {bundleProducts.pole && (
                  <ProductOptionsList
                    testid={DATA_TEST_IDS.BUNDLE_TYPE('bundle-pole')}
                    type="bundle"
                    prefix="pole"
                    options={bundleProducts.pole.options}
                    params={params}
                    colorImages={colorImages}
                    handleChange={handleChange}
                    productOptionsModals={productOptionsModals}
                    errors={optionsRequiredErrors}
                    optionsType={DATA_TEST_IDS.PRODUCT_OPTIONS_DROPDOWN}
                  />
                )}
              </div>
            ) : (
              bundleProducts.type === 'pkg-snowboard' &&
              bundleProducts.boot && (
                <ProductOptionsList
                  testid={DATA_TEST_IDS.BUNDLE_TYPE('pkg-snowboard')}
                  type="bundle"
                  prefix="boot"
                  options={bundleProducts.boot.options}
                  params={params}
                  colorImages={colorImages}
                  handleChange={handleChange}
                  productOptionsModals={productOptionsModals}
                  errors={optionsRequiredErrors}
                />
              )
            )}
          </div>
        ) : (
          <div className="space-y-[30px]">
            <ProductOptionsList
              testid={DATA_TEST_IDS.BUNDLE_TYPE('product')}
              type="product"
              options={options as OptionWithValues[]}
              availableOptions={availableOptions}
              discountedOptions={discountedOptions}
              params={params}
              colorImages={colorImages}
              handleChange={handleChange}
              productOptionsModals={productOptionsModals}
              hideIDontKnow
              isEveryVariantDiscontinued={isEveryVariantDiscontinued}
            />
            {additionalFieldsNormalized != null &&
              additionalFieldsNormalized.length > 0 && (
                <form
                  action={'#'}
                  ref={additionalFormRef}
                  onSubmit={preventDefault}
                >
                  {additionalFieldsNormalized?.map(
                    (fieldTitle: string, index: number) => {
                      return (
                        <div
                          key={fieldTitle}
                          className={clsx(index > 0 && 'mt-4')}
                        >
                          <ProductDetailHeader
                            title={fieldTitle?.toUpperCase()}
                            data={
                              productOptionsModals?.[fieldTitle.toUpperCase()]
                            }
                          />
                          <Input
                            data-testid={DATA_TEST_IDS.INPUT(fieldTitle)}
                            name={fieldTitle}
                            type={'text'}
                            className="md:w-[224px]"
                            variant={'default'}
                          />
                        </div>
                      );
                    },
                  )}
                </form>
              )}
          </div>
        )}
      </div>
      {productAddons && (
        <ProductAddons
          className={'mt-6'}
          titles={productAddonsTitle}
          products={productAddons}
          params={params}
          onClick={handleClickAddon}
          productAddonModals={productAddonModals}
        />
      )}
      <div className="grid items-stretch py-5 mt-3">
        <Button
          data-testid={DATA_TEST_IDS.BUTTON('add-to-cart')}
          id={ELEMENT_IDS.ADD_TO_CART}
          width={'full'}
          variant={isOutOfStock ? 'secondary' : 'blue'}
          size={'lg'}
          as={'button'}
          loading={isLoading}
          disabled={isOutOfStock || isLoading}
          onClick={() => handleClickAddToCart(false)}
        >
          {isOutOfStock ? (
            <TextDEPRECATED>
              {hasComingSoonTag ? 'Coming Soon' : 'Sold out'}
            </TextDEPRECATED>
          ) : (
            <TextDEPRECATED
              as="span"
              className="flex items-center justify-center gap-2"
            >
              <span>ADD TO CART</span>
            </TextDEPRECATED>
          )}
        </Button>

        {!isOutOfStock && (
          <>
            {isBOPISOnly && (
              <div className="italic text-16/20 mt-3 text-center">
                Only available for in-store pick up
              </div>
            )}
            <div className="flex justify-center">
              <button
                data-testid={DATA_TEST_IDS.BUTTON('open-modal')}
                type="button"
                className="flex items-center mt-[15px] border-b-2 border-b-black pb-1"
                onClick={() => setOpenStoreModal(true)}
              >
                <ImageHydrogen
                  loading="lazy"
                  src={iconStoreLocation}
                  alt="CHECK IN-STORE AVAILABILITY"
                  width={18}
                  height={15}
                  className="mr-2"
                />
                <span className="text-16/20 font-bold font-header text-blue-400">
                  CHECK IN-STORE AVAILABILITY
                </span>
              </button>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={openStoreModal}
        onCloseModal={() => setOpenStoreModal(false)}
        title={`OUR STORE${stores?.length !== 1 ? 'S' : ''}`}
        classNamePanel="max-w-[1272px] p-5 lg:pt-10 lg:pr-10 lg:pl-[131px] lg:pb-[131px]"
        classNameTitle="mt-[18px] lg:mt-5"
      >
        <div className="space-y-[60px] mt-2.5 lg:mt-[30px]">
          {stores?.map((store) => (
            <div
              key={store.name}
              className="flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-[38px]"
            >
              <div className="w-full lg:w-[486px] aspect-square bg-gray-200 rounded-[10px] overflow-hidden">
                {store.image && store.image.asset?._ref && (
                  <SanityImage
                    alt={store.image.altText}
                    crop={store.image.crop}
                    hotspot={store.image.hotspot}
                    src={store.image.asset._ref}
                    layout="fill"
                    breakpoints={[[486, 486]]}
                  />
                )}
              </div>
              <div>
                <div className="text-22/22 lg:text-28/28 font-header font-bold">
                  {store.title}
                </div>

                <div className="flex mt-[15px]">
                  <ImageHydrogen
                    loading="lazy"
                    src={iconStoreLocation}
                    alt="CHECK IN-STORE AVAILABILITY"
                    width={18}
                    height={15}
                    className="mr-2.5"
                  />
                  <span className="text-16/20 font-bold font-header text-blue-400">
                    PICKUP AVAILABLE
                  </span>
                </div>

                {(store.address1 || store.address2 || store.address3) && (
                  <div className="mt-[30px] text-16/24 lg:text-18/26">
                    {store.address1 && <div>{store.address1}</div>}
                    {store.address2 && <div>{store.address2}</div>}
                    {store.address3 && <div>{store.address3}</div>}
                  </div>
                )}

                {store.phone && (
                  <div className="mt-2.5 text-16/24 lg:text-18/26">
                    {store.phone}
                  </div>
                )}

                {store.openingHours && store.openingHours.length > 0 && (
                  <div className="mt-2.5">
                    {store.openingHours.map((openingHour) => (
                      <div key={openingHour.title} className="mb-2.5">
                        <div className="text-16/24 lg:text-18/26">
                          {openingHour.title}
                        </div>
                        <div className="text-16/20 lg:text-16/20 font-header font-bold mt-[2px]">
                          {openingHour.time}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={showBootTreatmentModal}
        onCloseModal={() => setShowBootTreatmentModal(false)}
        title={siteSettings?.bootTreatmentModal?.title ?? ''}
        classNamePanel="max-w-[1272px] p-5 pb-[65px] lg:pt-10 lg:pr-10 lg:pl-[131px] lg:pb-10"
        classNameTitle="mt-5"
        classNameContent="mt-[14px] lg:mt-5"
      >
        <div className="text-16/24 lg:text-18/26 max-w-[748px]">
          {siteSettings?.bootTreatmentModal?.description}
        </div>

        <div className="mt-[30px] grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-[54px] lg:gap-[38px] lg:pr-[91px]">
          {bootsOptions.map((boot, index) => {
            const isSelected = index === bootTreatmentSelectedBoot;

            return (
              <button
                data-testid={DATA_TEST_IDS.BUTTON('boot-treatment')}
                key={boot.node.id}
                className="text-left"
                type="button"
                onClick={() => setBootTreatmentSelectedBoot(index)}
              >
                <div
                  className={clsx(
                    'border-2  rounded-[10px] overflow-hidden w-full aspect-square relative grid place-items-center',
                    isSelected ? 'border-blue-500' : 'border-white',
                  )}
                >
                  {boot.node.featuredImage?.url && (
                    <ImageHydrogen
                      loading="lazy"
                      src={boot.node.featuredImage.url}
                      alt={boot.node.title}
                      width={204}
                      height={204}
                    />
                  )}

                  {isSelected && (
                    <div className="w-[30px] lg:w-[43px] h-[30px] lg:h-[43px] rounded-full grid place-content-center flex-shrink-0 bg-blue-500 absolute top-[8px] lg:top-[6px] right-[8px] lg:right-[6px]">
                      <img
                        loading="lazy"
                        data-testid={DATA_TEST_IDS.CHECKBOX('checked')}
                        src={iconCheck}
                        alt="Boot selected"
                      />
                    </div>
                  )}
                </div>

                <div className="text-11/15 lg:text-13/15 font-bold font-header text-blue-500 mt-[5px] lg:mt-2.5 h-4">
                  {index === 0 && 'RECOMMENDED'}
                </div>

                <div className="text-14/16 lg:text-20/26 font-bold font-header mt-[5px] line-clamp-2">
                  {boot.node.title}
                </div>
              </button>
            );
          })}

          <button
            data-testid={DATA_TEST_IDS.BUTTON('i-dont-know')}
            type="button"
            className={clsx(
              'font-header text-left relative border-2 font-bold text-16/20 lg:text-20/26 text-gray-400 bg-gray-200 rounded-[10px] aspect-square grid place-items-center px-[24px] lg:px-[31px]',
              bootTreatmentSelectedBoot === -1
                ? 'border-blue-500'
                : 'border-gray-200',
            )}
            onClick={() => setBootTreatmentSelectedBoot(-1)}
          >
            I DON’T KNOW, PLEASE CONTACT ME
            {bootTreatmentSelectedBoot === -1 && (
              <div className="w-[30px] lg:w-[43px] h-[30px] lg:h-[43px] rounded-full grid place-content-center flex-shrink-0 bg-blue-500 absolute top-[8px] lg:top-[6px] right-[8px] lg:right-[6px]">
                <img
                  data-testid={DATA_TEST_IDS.CHECKBOX('checked')}
                  loading="lazy"
                  src={iconCheck}
                  alt="I don't know option selected"
                />
              </div>
            )}
          </button>
        </div>

        <div className="mt-[46px] lg:mt-[201px] lg:mr-[90px] flex justify-center">
          <div className="max-w-[486px] w-full">
            <Button
              data-testid={DATA_TEST_IDS.BUTTON('add-to-cart')}
              id={ELEMENT_IDS.ADD_TO_CART}
              variant={isOutOfStock ? 'secondary' : 'blue'}
              width={'full'}
              size={'lg'}
              as={'button'}
              loading={isLoading}
              disabled={isOutOfStock || isLoading}
              onClick={() => {
                setShowBootTreatmentModal(false);
                // Wait for the modal to close because the scroll is locked after close the cart drawer
                setTimeout(() => handleClickAddToCart(true), 500);
              }}
            >
              {isOutOfStock ? (
                <TextDEPRECATED>Sold out</TextDEPRECATED>
              ) : (
                <TextDEPRECATED
                  as="span"
                  className="flex items-center justify-center gap-2"
                >
                  <span>ADD TO CART</span>
                </TextDEPRECATED>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ProductOptionsList({
  type,
  options,
  availableOptions,
  discountedOptions,
  params,
  productOptionsModals,
  colorImages,
  errors,
  hideIDontKnow,
  handleChange,
  testid,
  isEveryVariantDiscontinued,
  optionsType,
}: {
  type: 'product' | 'bundle';
  prefix?: string;
  options: OptionWithValues[];
  availableOptions?: Record<string, string[]>;
  discountedOptions?: Record<string, string[]>;
  params: URLSearchParams;
  productOptionsModals?: Record<string, ContentModalEntity>;
  colorImages?: Record<string, Image>;
  errors?: Record<string, boolean>;
  hideIDontKnow?: boolean;
  handleChange: (name: string, value: string) => void;
  testid?: string;
  isEveryVariantDiscontinued?: boolean;
  optionsType?: string;
}) {
  return (
    <div data-testid={testid} className="flex-1 space-y-[30px]">
      {(options as OptionWithValues[]).map(({name, values}) => {
        if (type === 'bundle' && values.length === 0) {
          return null;
        }
        if (values.length === 1 && values[0] === 'Default Title') {
          return null;
        }
        if (
          name.toLowerCase() === 'color' &&
          values.length === 1 &&
          isEveryVariantDiscontinued
        ) {
          return null;
        }

        const activeValue = params.get(encodeURIComponent(name.toLowerCase()));
        const dataModal = productOptionsModals?.[name.toUpperCase()];

        return (
          <ProductOptionsContainer
            key={name}
            name={name}
            title={
              type === 'product'
                ? `SELECT ${name.toUpperCase()}`
                : name.toUpperCase()
            }
            values={
              hideIDontKnow
                ? values.filter((v) => v !== I_DONT_KNOW_OPTION)
                : values
            }
            availableValues={availableOptions?.[name] ?? values}
            discountedValues={discountedOptions?.[name] ?? []}
            activeValue={activeValue}
            colorImages={colorImages}
            dataModal={dataModal}
            handleChange={handleChange}
            error={errors?.[name]}
            optionsType={optionsType}
          />
        );
      })}
    </div>
  );
}

function ProductOptionsContainer({
  name,
  title,
  values,
  availableValues,
  discountedValues,
  activeValue,
  colorImages,
  dataModal,
  error,
  handleChange,
  optionsType,
}: {
  name: string;
  title: string;
  values: string[];
  availableValues: string[];
  discountedValues: string[];
  activeValue: string | null;
  colorImages?: Record<string, Image>;
  dataModal?: ContentModalEntity;
  error?: boolean;
  handleChange: (name: string, value: string) => void;
  optionsType?: string;
}) {
  const isSize = name.includes('size');
  const headerRight = isSize && dataModal && (
    <SizeGuideModal data={dataModal} />
  );

  return (
    <div key={name} className="flex flex-col flex-wrap">
      <ProductDetailHeader
        title={title}
        headerRight={headerRight}
        data={!isSize ? dataModal : undefined}
        name={name}
        activeValue={activeValue}
      />
      <div className="flex flex-wrap items-baseline">
        <ProductOptions
          name={name}
          values={values}
          availableValues={availableValues}
          discountedValues={discountedValues}
          handleChange={handleChange}
          colorImages={colorImages}
          activeValue={activeValue}
          optionsType={optionsType ?? DATA_TEST_IDS.PRODUCT_OPTIONS_SWATCH}
          error={error}
        />
      </div>
    </div>
  );
}
